/**
 * Accountbeheer: uurtarieven per arbeidsprofiel (tenant defaults → alle dossiers, export, offerte).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Euro, Loader2 } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

export type LaborProfileRateRow = {
  labor_profile_id: string;
  name: string;
  discipline: string;
  default_hourly_rate: number;
  override_hourly_rate: number | null;
  effective_hourly_rate: number;
};

export function AccountLaborRates() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-labor-rates', tenantId],
    queryFn: () => api.getTenantLaborRates(tenantId),
    enabled: !!user?.authenticated,
  });

  const profiles = data?.profiles ?? [];

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [justSaved, setJustSaved] = useState(false);

  const displayValue = (p: LaborProfileRateRow) => {
    const d = draft[p.labor_profile_id];
    if (d !== undefined) return d;
    if (p.override_hourly_rate != null) return String(p.override_hourly_rate);
    return '';
  };

  const mut = useMutation({
    mutationFn: async () => {
      const rates: Record<string, number | null> = {};
      for (const [id, raw] of Object.entries(draft)) {
        const t = raw.trim();
        if (t === '') rates[id] = null;
        else {
          const n = Number(t.replace(',', '.'));
          if (!Number.isFinite(n) || n < 0 || n > 5000) {
            throw new Error('Tarief moet tussen 0 en 5000 liggen (of leeg voor standaard).');
          }
          rates[id] = Math.round(n * 100) / 100;
        }
      }
      if (Object.keys(rates).length === 0) return null;
      return api.putTenantLaborRates(tenantId, { rates });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tenant-labor-rates', tenantId] });
      await qc.invalidateQueries({ queryKey: ['projects'] });
      setDraft({});
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 5000);
    },
  });

  const errMsg = error instanceof Error ? error.message : error ? String(error) : '';

  return (
    <div className="animate-page-enter pb-10">
      <SeoMeta
        path="/account/labor-rates"
        noindex
        title="Uurtarieven organisatie"
        description="Standaard uurtarieven voor calculaties en offertes — CalqTool."
      />
      <Breadcrumbs items={[{ label: 'Account', href: '/account' }, { label: 'Uurtarieven' }]} />

      <div className="mb-6">
        <Link
          to="/account"
          className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Terug naar account
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <Euro className="h-8 w-8 text-primary" aria-hidden />
          <div>
            <h1 className="text-2xl font-bold text-text">Uurtarieven (organisatie)</h1>
            <p className="mt-1 max-w-2xl text-sm text-text-muted">
              Pas <strong className="text-text">jouw</strong> uurtarieven aan per vak. Ze gelden voor alle dossiers,
              totalen, export en offertes van tenant <code className="rounded bg-surface px-1 text-xs">{tenantId}</code>.
              Leeg veld + opslaan = terug naar bibliotheekstandaard voor die regel (als je eerder een override had).
            </p>
          </div>
        </div>
      </div>

      {isLoading && (
        <p className="flex items-center gap-2 text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          Laden…
        </p>
      )}
      {error && (
        <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200" role="alert">
          {errMsg}
        </p>
      )}

      {profiles.length > 0 && (
        <form
          className="overflow-x-auto rounded-xl border border-border bg-surface-card shadow-sm"
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
        >
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/80 text-text-muted">
                <th className="px-4 py-3 font-medium">Functie</th>
                <th className="px-4 py-3 font-medium">Discipline</th>
                <th className="px-4 py-3 font-medium">Standaard €/u</th>
                <th className="px-4 py-3 font-medium">Jouw €/u</th>
                <th className="px-4 py-3 font-medium">Effectief</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => {
                const dr = draft[p.labor_profile_id];
                let eff = p.effective_hourly_rate;
                if (dr !== undefined) {
                  const t = dr.trim();
                  if (t === '') eff = p.default_hourly_rate;
                  else {
                    const n = Number(t.replace(',', '.'));
                    if (Number.isFinite(n)) eff = n;
                  }
                }
                return (
                  <tr key={p.labor_profile_id} className="border-b border-border/70">
                    <td className="px-4 py-2.5 font-medium text-text">{p.name}</td>
                    <td className="px-4 py-2.5 text-text-muted">{p.discipline}</td>
                    <td className="px-4 py-2.5 tabular-nums text-text-muted">{p.default_hourly_rate.toFixed(2)}</td>
                    <td className="px-4 py-2.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={p.override_hourly_rate != null ? String(p.override_hourly_rate) : '—'}
                        aria-label={`Uurtarief ${p.name}`}
                        className="w-28 rounded-lg border border-border bg-surface px-2 py-1.5 tabular-nums text-text"
                        value={displayValue(p)}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, [p.labor_profile_id]: e.target.value }))
                        }
                      />
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-text">{eff.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-4 border-t border-border p-4">
            {mut.isError && (
              <p className="text-sm text-red-600">{mut.error instanceof Error ? mut.error.message : 'Opslaan mislukt'}</p>
            )}
            {justSaved && (
              <p className="text-sm text-green-700 dark:text-green-400">Opgeslagen.</p>
            )}
            <button
              type="submit"
              disabled={mut.isPending || Object.keys(draft).length === 0}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {mut.isPending ? 'Opslaan…' : 'Wijzigingen opslaan'}
            </button>
            {Object.keys(draft).length > 0 && (
              <button
                type="button"
                className="text-sm text-text-muted underline"
                onClick={() => setDraft({})}
              >
                Draft wissen
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
